import { useEffect, useState } from "react";
import { Crown, Medal, Sparkles, Trophy, User } from "lucide-react";

import { toast } from "@/components/ui/sonner";
import { fetchLeaderboard } from "@/lib/api";
import { pickText } from "@/lib/content";

const RANK_STYLES = {
  1: { bg: "from-amber-900/40 to-amber-950/20", border: "border-amber-400/40", icon: Crown, iconColor: "text-amber-400" },
  2: { bg: "from-slate-400/20 to-slate-600/10", border: "border-slate-400/40", icon: Medal, iconColor: "text-slate-300" },
  3: { bg: "from-orange-800/30 to-orange-950/10", border: "border-orange-400/40", icon: Medal, iconColor: "text-orange-400" },
};

export const LeaderboardPage = ({ language, vipEmail }) => {
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLeaderboard = async () => {
      if (!vipEmail) return;
      try {
        setLoading(true);
        const data = await fetchLeaderboard(vipEmail, 20);
        setLeaderboard(data);
      } catch (error) {
        console.error(error);
        toast.error(pickText(language, "Unable to load leaderboard.", "Impossibile caricare la classifica."));
      } finally {
        setLoading(false);
      }
    };
    loadLeaderboard();
  }, [language, vipEmail]);

  return (
    <div className="space-y-10" data-testid="leaderboard-page">
      <div className="max-w-3xl space-y-4">
        <p className="section-label" data-testid="leaderboard-eyebrow">
          {pickText(language, "Temple Leaderboard", "Classifica del Tempio")}
        </p>
        <h2 className="text-5xl text-foreground md:text-6xl" data-testid="leaderboard-title">
          {pickText(language, "Masters of the sacred archive.", "Maestri dell'archivio sacro.")}
        </h2>
        <p className="body-copy" data-testid="leaderboard-description">
          {pickText(language, "VIP members ranked by achievement points. Explore, engage, and rise through the temple ranks.", "Membri VIP classificati per punti achievement. Esplora, interagisci e sali nei ranghi del tempio.")}
        </p>
      </div>

      {/* User's Rank Card */}
      {leaderboard?.user_rank && (
        <div className="glass-card rounded-[2rem] border-primary/30 p-6" data-testid="leaderboard-user-rank">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
                <User className="h-8 w-8 text-primary" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">
                  {pickText(language, "Your Rank", "La Tua Posizione")}
                </p>
                <p className="mt-1 text-4xl text-foreground" data-testid="leaderboard-user-position">
                  #{leaderboard.user_rank}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-3xl text-primary">
              <Sparkles className="h-6 w-6" />
              <span data-testid="leaderboard-user-points">{leaderboard.user_points}</span>
              <span className="text-sm text-slate-400">pts</span>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard Table */}
      <div className="glass-card rounded-[2rem] p-6" data-testid="leaderboard-table">
        <div className="flex items-center gap-3 text-primary">
          <Trophy className="h-5 w-5" />
          <p className="section-label">{pickText(language, "Top Temple Members", "Migliori Membri del Tempio")}</p>
        </div>

        {loading ? (
          <div className="mt-6 rounded-3xl border border-white/10 bg-black/20 p-6 text-center text-sm text-slate-300">
            {pickText(language, "Loading leaderboard...", "Caricamento classifica...")}
          </div>
        ) : leaderboard?.entries?.length === 0 ? (
          <div className="mt-6 rounded-3xl border border-dashed border-white/10 bg-black/20 p-6 text-center text-sm text-slate-400">
            {pickText(language, "No rankings yet. Be the first to earn achievements!", "Nessuna classifica ancora. Sii il primo a guadagnare achievement!")}
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {leaderboard?.entries?.map((entry) => {
              const rankStyle = RANK_STYLES[entry.rank] || { bg: "from-white/5 to-transparent", border: "border-white/10", icon: null, iconColor: "text-slate-400" };
              const RankIcon = rankStyle.icon;

              return (
                <div
                  className={`flex items-center gap-4 rounded-[1.5rem] border bg-gradient-to-r p-4 ${rankStyle.border} ${rankStyle.bg}`}
                  data-testid={`leaderboard-entry-${entry.rank}`}
                  key={entry.rank}
                >
                  {/* Rank */}
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/10 bg-black/30">
                    {RankIcon ? (
                      <RankIcon className={`h-6 w-6 ${rankStyle.iconColor}`} />
                    ) : (
                      <span className="text-lg font-bold text-slate-400">#{entry.rank}</span>
                    )}
                  </div>

                  {/* User Info */}
                  <div className="flex-1">
                    <p className="text-lg text-foreground">{entry.display_name}</p>
                    {entry.top_achievement && (
                      <p className="text-xs text-slate-400">
                        🏆 {entry.top_achievement}
                      </p>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="text-right">
                    <p className="text-2xl text-primary">{entry.total_points}</p>
                    <p className="text-xs text-slate-500">
                      {entry.unlocked_count} {pickText(language, "badges", "badge")}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Motivational Section */}
      <div className="rounded-[2rem] border border-white/5 bg-gradient-to-br from-primary/5 to-transparent p-8 text-center">
        <Sparkles className="mx-auto h-8 w-8 text-primary/60" />
        <p className="mt-4 text-lg text-slate-300">
          {pickText(language, "Earn points by exploring the Atlas, chatting with Kōnshus, and engaging with the community.", "Guadagna punti esplorando l'Atlante, chattando con Kōnshus e interagendo con la comunità.")}
        </p>
      </div>
    </div>
  );
};
